import {
  DecisionsHubViewSchema,
  SourceRefSchema,
  type DecisionsHubDecision,
  type DecisionsHubGroup,
  type DecisionsHubOption,
  type DecisionsHubResolutionKind,
  type DecisionsHubView,
  type OverrideStructuredChange,
  type SourceRef
} from "@automatry/schemas";

import { solutionDisplayName } from "../app/format";
import type { ProjectFixtureForDisplay } from "./projectCockpitAdapter";
import type { ApartmentSolutionForDisplay } from "./solutionViewAdapter";

type ViewStatus = "loading" | "ready" | "blocked" | "error";
type SolutionRisk = ApartmentSolutionForDisplay["risks"][number];
type EdgeFeature = NonNullable<ApartmentSolutionForDisplay["edgeFeatures"]>[number];
type OpenDecisionStage = "lane-a" | "lane-b" | "output";

interface OpenDecisionForDisplay {
  id: string;
  stage: OpenDecisionStage;
  subjectRef?: string;
  message?: string;
}

interface DecisionRiskRecord extends SolutionRisk {
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  linkedOpenDecision?: OpenDecisionForDisplay;
  linkedEdgeFeature?: EdgeFeature;
}

interface DecisionBucket {
  key: string;
  theme: string;
  themeLabel: string;
  records: DecisionRiskRecord[];
}

export interface DecisionsHubDisplayViewModel extends DecisionsHubView {
  decisions: DecisionsHubDecision[];
  groups: DecisionsHubGroup[];
  generatedFrom: string;
}

const labelise = (value: string): string =>
  value
    .replace(/^carrington-/, "")
    .replace(/^type-/, "Type ")
    .replace(/^selector-/, "")
    .replace(/^edge-/, "Edge ")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const displayNameForProductId = (value: string): string => {
  const productLabels: Record<string, string> = {
    bms_metering_exposure: "BMS metering interface",
    by_others_electricity_meter: "By-others electricity meter",
    by_others_heat_cool_meter: "By-others heat/cool meter",
    cloud_service: "Cloud service",
    control_panel_enclosure: "Control panel enclosure",
    edge_cloud_service: "Edge gateway",
    edge_compute: "Edge gateway",
    edge_enclosure: "Edge gateway",
    edge_network_switch: "Edge gateway",
    metering_netmaster_panel: "Metering Netmaster panel",
    netmaster: "Netmaster"
  };
  const key = value
    .trim()
    .toLowerCase()
    .replace(/[.?!]+$/g, "");

  return productLabels[key] ?? labelise(key);
};

const humaniseDecisionText = (value: string): string => {
  const productIdPattern =
    /\b(bms_metering_exposure|by_others_electricity_meter|by_others_heat_cool_meter|cloud_service|control_panel_enclosure|edge_cloud_service|edge_compute|edge_enclosure|edge_network_switch|metering_netmaster_panel|netmaster)\b/gi;

  return value
    .replace(productIdPattern, (match) => displayNameForProductId(match))
    .replace(/\s+/g, " ")
    .trim();
};

const sentenceCase = (value: string): string =>
  value ? `${value[0]?.toUpperCase()}${value.slice(1)}` : value;

const safeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);

const viewStatus = (
  status: ProjectFixtureForDisplay["project"]["outputBundle"]["status"]
): ViewStatus => (status === "ready" ? "ready" : "blocked");

const sourceRefs = (refs: readonly unknown[] | undefined): SourceRef[] =>
  (refs ?? []).flatMap((ref) => {
    const parsed = SourceRefSchema.safeParse(ref);

    return parsed.success ? [parsed.data] : [];
  });

const sourceRefKey = (ref: SourceRef): string =>
  [ref.sourceId, ref.locator, ref.quote ?? "", ref.rangeRef ?? "", ref.hash ?? ""].join(
    "|"
  );

const uniqueSourceRefs = (records: readonly DecisionRiskRecord[]): SourceRef[] => {
  const refs = new Map<string, SourceRef>();

  for (const record of records) {
    for (const ref of sourceRefs(record.sourceRefs)) {
      refs.set(sourceRefKey(ref), ref);
    }

    if (record.linkedEdgeFeature) {
      for (const ref of sourceRefs(record.linkedEdgeFeature.sourceRefs)) {
        refs.set(sourceRefKey(ref), ref);
      }
    }
  }

  return [...refs.values()];
};

const uniqueRuleTraceRefs = (records: readonly DecisionRiskRecord[]): string[] => [
  ...new Set(
    records.flatMap((record) => [
      ...(record.ruleTraceRefs ?? []),
      ...(record.linkedEdgeFeature?.ruleTraceRefs ?? [])
    ])
  )
];

const decisionText = (record: DecisionRiskRecord): string =>
  [
    record.id,
    record.title,
    record.plainEnglish,
    record.linkedOpenDecision?.message,
    record.recommendedActions?.map((action) => action.action).join(" "),
    record.ruleTraceRefs?.join(" "),
    record.linkedEdgeFeature?.featureKind,
    record.linkedEdgeFeature?.inputPolicy
  ]
    .filter(Boolean)
    .join(" ");

const themeFor = (record: DecisionRiskRecord): { theme: string; label: string } => {
  const text = decisionText(record).toLowerCase();

  if (text.includes("edge") || text.includes("leak")) {
    return { theme: "edge", label: "Edge" };
  }

  if (text.includes("meter")) {
    return { theme: "metering-topology", label: "Metering topology" };
  }

  if (text.includes("panel") || text.includes("enclosure") || text.includes("tier")) {
    return { theme: "panel-enclosure-tier", label: "Panel/enclosure tier" };
  }

  if (
    text.includes("screen") ||
    text.includes("rdu") ||
    text.includes("ts-4") ||
    text.includes("ts4") ||
    text.includes("ts-7") ||
    text.includes("ts7") ||
    text.includes("ui ")
  ) {
    return { theme: "ui-tier", label: "UI tier" };
  }

  if (text.includes("relay") || text.includes("srm")) {
    return { theme: "relay-family", label: "Relay family" };
  }

  if (text.includes("controller") || text.includes("710") || text.includes("fcu")) {
    return { theme: "controller-generation", label: "Controller generation" };
  }

  if (
    text.includes("product") ||
    text.includes("sku") ||
    text.includes("catalog") ||
    text.includes("placeholder")
  ) {
    return { theme: "product-selection", label: "Product selection" };
  }

  return { theme: "other", label: "Other decisions" };
};

const normaliseDecisionKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/carrington[- ]type[- ][a-z]/g, "apartment-type")
    .replace(/\s+/g, " ")
    .replace(/[.?!]+$/g, "")
    .trim();

const primaryAction = (records: readonly DecisionRiskRecord[]): string | undefined =>
  records.find((record) => record.recommendedActions?.[0])?.recommendedActions?.[0]
    ?.action;

const bucketKeyFor = (record: DecisionRiskRecord): string => {
  const theme = themeFor(record);
  const action = record.recommendedActions?.[0]?.action;
  const source = action ?? record.linkedOpenDecision?.message ?? record.plainEnglish;

  return `${theme.theme}:${normaliseDecisionKey(source)}`;
};

const affectedApartmentTypeIds = (records: readonly DecisionRiskRecord[]): string[] => [
  ...new Set(records.map((record) => record.scope?.refId ?? record.apartmentTypeId))
];

const affectedApartmentTypeLabels = (
  records: readonly DecisionRiskRecord[],
  projectId: string
): string[] => [
  ...new Set(
    records.map((record) =>
      solutionDisplayName(record.scope?.refId ?? record.apartmentTypeId, { projectId })
    )
  )
];

const hasLeakInputPolicyDecision = (text: string): boolean =>
  text.includes("reuse existing titan") ||
  text.includes("dedicated leak") ||
  text.includes("leak sensor");

const hasProductMappingGap = (text: string): boolean =>
  text.includes("no existing capability") ||
  text.includes("no known catalog") ||
  text.includes("placeholder") ||
  text.includes("no reviewed catalog");

const hasUiTierDecision = (text: string): boolean =>
  text.includes("rdu") ||
  text.includes("ts-4") ||
  text.includes("ts4") ||
  text.includes("ts-7") ||
  text.includes("ts7") ||
  text.includes("screen");

const hasByOthersMeterProductDecision = (text: string): boolean =>
  (text.includes("by-others") || text.includes("by others")) &&
  text.includes("meter") &&
  (text.includes("meter product") ||
    text.includes("meter sku") ||
    text.includes("product identities") ||
    text.includes("modbus meter"));

const optionId = (decisionId: string, label: string): string =>
  `${decisionId}-option-${safeId(label)}`;

const makeUiStrategyOption = ({
  decisionId,
  label,
  value,
  engineDefault,
  affectedApartmentTypeId
}: {
  decisionId: string;
  label: string;
  value: "ts4" | "ts7" | "rdu";
  engineDefault: boolean;
  affectedApartmentTypeId: string | undefined;
}): DecisionsHubOption => ({
  id: optionId(decisionId, label),
  label,
  description: `Re-derive the affected apartment type with ui_screen_kind = ${value}.`,
  engineDefault,
  resolutionKind: "strategy_input",
  resolutionStatus: "resolvable",
  structuredChange: {
    kind: "set_strategy_input",
    input: "ui_screen_kind",
    value
  },
  ...(affectedApartmentTypeId ? { apartmentTypeId: affectedApartmentTypeId } : {})
});

const genericActionOption = ({
  decisionId,
  action,
  description
}: {
  decisionId: string;
  action: string;
  description: string;
}): DecisionsHubOption => ({
  id: optionId(decisionId, action),
  label: humaniseDecisionText(action).replace(/[.?!]+$/g, ""),
  description,
  engineDefault: false,
  resolutionKind: "not_wired",
  resolutionStatus: "not_wired"
});

const optionsForDecision = ({
  decisionId,
  records,
  text
}: {
  decisionId: string;
  records: readonly DecisionRiskRecord[];
  text: string;
}): DecisionsHubOption[] => {
  const lower = text.toLowerCase();
  const firstAffected = affectedApartmentTypeIds(records)[0];

  if (hasUiTierDecision(lower)) {
    return [
      makeUiStrategyOption({
        decisionId,
        label: "Keep TS-4 network-screen strategy",
        value: "ts4",
        engineDefault: true,
        affectedApartmentTypeId: firstAffected
      }),
      makeUiStrategyOption({
        decisionId,
        label: "Use RDU direct-wired strategy",
        value: "rdu",
        engineDefault: false,
        affectedApartmentTypeId: firstAffected
      }),
      makeUiStrategyOption({
        decisionId,
        label: "Use TS-7 screen strategy",
        value: "ts7",
        engineDefault: false,
        affectedApartmentTypeId: firstAffected
      })
    ];
  }

  if (hasLeakInputPolicyDecision(lower)) {
    return [
      {
        id: optionId(decisionId, "Reuse existing Titan/BACnet inputs"),
        label: "Reuse existing Titan/BACnet inputs",
        description:
          "This is the engine's current model; persistence of the decision is not wired yet.",
        engineDefault: true,
        resolutionKind: "not_wired",
        resolutionStatus: "not_wired"
      },
      {
        id: optionId(decisionId, "Add dedicated leak sensor hardware"),
        label: "Add dedicated leak sensor hardware",
        description:
          "No reviewed leak-sensor SKU or structured edge input override is emitted yet.",
        engineDefault: false,
        resolutionKind: "not_wired",
        resolutionStatus: "not_wired"
      }
    ];
  }

  const action = primaryAction(records);

  return action
    ? [
        genericActionOption({
          decisionId,
          action,
          description:
            "The engine emitted an action but no structured override or reviewed option for this decision."
        })
      ]
    : [];
};

const resolutionKindFor = (
  options: readonly DecisionsHubOption[]
): DecisionsHubResolutionKind =>
  options.find((option) => option.resolutionStatus === "resolvable")?.resolutionKind ??
  "not_wired";

const resolutionNotWiredReason = (
  options: readonly DecisionsHubOption[],
  text: string
): string | undefined => {
  if (options.some((option) => option.resolutionStatus === "resolvable")) {
    return undefined;
  }

  if (hasProductMappingGap(text.toLowerCase())) {
    return "Shown for review only: no reviewed product exists, so the UI will not invent one.";
  }

  return "Shown for review only: this decision has no live edit path in the current project workspace.";
};

const decisionTitle = (records: readonly DecisionRiskRecord[]): string => {
  const action = primaryAction(records);
  const match = action?.match(/product mapping for ([a-z0-9_/-]+)/i);

  if (match?.[1]) {
    return `${displayNameForProductId(match[1])} product mapping`;
  }

  if (action?.toLowerCase().startsWith("confirm whether ")) {
    return sentenceCase(
      humaniseDecisionText(
        action.replace(/^confirm whether /i, "").replace(/[.?!]+$/g, "")
      )
    );
  }

  const first = records[0];

  return first ? humaniseDecisionText(first.title) : "Needs user decision";
};

const decisionWhat = (records: readonly DecisionRiskRecord[]): string => {
  const action = primaryAction(records);

  if (action) {
    return humaniseDecisionText(action).replace(/[.?!]+$/g, "");
  }

  return records[0]?.title
    ? humaniseDecisionText(records[0].title)
    : "Internal decision required";
};

const engineDefaultFor = (
  records: readonly DecisionRiskRecord[],
  text: string,
  options: readonly DecisionsHubOption[]
): string => {
  const lower = text.toLowerCase();
  const defaultOption = options.find((option) => option.engineDefault);

  if (defaultOption) {
    return humaniseDecisionText(defaultOption.label);
  }

  if (hasProductMappingGap(lower)) {
    return "Placeholder only; no reviewed product selected.";
  }

  if (hasByOthersMeterProductDecision(lower)) {
    return "No meter product is selected yet; the current spec keeps the meters as by-others Modbus devices until the exact electricity and heat/cool models are confirmed.";
  }

  if (hasLeakInputPolicyDecision(lower)) {
    return "Reuse the existing Titan/BACnet input estate; dedicated sensors remain to confirm.";
  }

  if (hasUiTierDecision(lower)) {
    return "Keep the generated UI strategy unless the specifier selects a wired override.";
  }

  return "Keep the generated spec unchanged until this decision is confirmed.";
};

const sourceLabels = (refs: readonly SourceRef[]): string[] =>
  refs.slice(0, 4).map((ref) => ref.sourceId);

const dataGapsFor = ({
  options,
  text,
  sourceRefs
}: {
  options: readonly DecisionsHubOption[];
  text: string;
  sourceRefs: readonly SourceRef[];
}): string[] => [
  ...(options.length === 0
    ? ["Decision options are not emitted by the engine for this row."]
    : []),
  ...(options.every((option) => option.resolutionStatus === "not_wired")
    ? ["No live edit option is available for this decision in the project workspace."]
    : []),
  ...(hasProductMappingGap(text.toLowerCase())
    ? ["Reviewed product option is not available; placeholder remains honest."]
    : []),
  ...(sourceRefs.length === 0 ? ["Source refs are not emitted for this decision."] : [])
];

const decisionFromBucket = (
  bucket: DecisionBucket,
  projectId: string
): DecisionsHubDecision => {
  const text = bucket.records.map(decisionText).join(" ");
  const id = `decision-${safeId(bucket.key)}`;
  const options = optionsForDecision({
    decisionId: id,
    records: bucket.records,
    text
  });
  const refs = uniqueSourceRefs(bucket.records);
  const ruleTraceRefs = uniqueRuleTraceRefs(bucket.records);
  const apartmentTypeIds = affectedApartmentTypeIds(bucket.records);
  const apartmentTypeLabels = affectedApartmentTypeLabels(bucket.records, projectId);
  const resolutionKind = resolutionKindFor(options);
  const resolutionStatus = options.some(
    (option) => option.resolutionStatus === "resolvable"
  )
    ? "resolvable"
    : "surfaced_only";

  return {
    id,
    title: decisionTitle(bucket.records),
    theme: bucket.theme,
    themeLabel: bucket.themeLabel,
    what: decisionWhat(bucket.records),
    whyItMatters:
      bucket.records[0]?.linkedOpenDecision?.message ??
      bucket.records[0]?.plainEnglish ??
      "Decision impact is not emitted by the engine.",
    engineDefault: engineDefaultFor(bucket.records, text, options),
    resolutionStatus,
    resolutionKind,
    ...(resolutionNotWiredReason(options, text)
      ? { resolutionNotWiredReason: resolutionNotWiredReason(options, text) }
      : {}),
    riskIds: bucket.records.map((record) => record.id),
    openDecisionIds: [
      ...new Set(
        bucket.records.flatMap((record) =>
          record.linkedOpenDecision ? [record.linkedOpenDecision.id] : []
        )
      )
    ],
    edgeFeatureIds: [
      ...new Set(
        bucket.records.flatMap((record) =>
          record.linkedEdgeFeature ? [record.linkedEdgeFeature.id] : []
        )
      )
    ],
    affectedApartmentTypeIds: apartmentTypeIds,
    affectedApartmentTypeLabels: apartmentTypeLabels,
    affectedApartmentTypeCount: apartmentTypeIds.length,
    optionCount: options.length,
    options,
    sourceRefCount: refs.length,
    sourceRefs: refs,
    ruleTraceRefs,
    dataGaps: dataGapsFor({ options, text, sourceRefs: refs })
  };
};

const groupDecisions = (
  decisions: readonly DecisionsHubDecision[]
): DecisionsHubGroup[] => {
  const byTheme = new Map<string, DecisionsHubDecision[]>();

  for (const decision of decisions) {
    byTheme.set(decision.theme, [...(byTheme.get(decision.theme) ?? []), decision]);
  }

  return [...byTheme.entries()]
    .map(([theme, items]) => ({
      id: theme,
      label: items[0]?.themeLabel ?? labelise(theme),
      decisionIds: items.map((item) => item.id),
      count: items.length,
      affectedApartmentTypeCount: new Set(
        items.flatMap((item) => item.affectedApartmentTypeIds)
      ).size,
      resolvableCount: items.filter((item) => item.resolutionStatus === "resolvable")
        .length,
      surfacedOnlyCount: items.filter(
        (item) => item.resolutionStatus === "surfaced_only"
      ).length
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

const detailedDecisionRisks = (
  fixture: ProjectFixtureForDisplay
): DecisionRiskRecord[] => {
  const projectId = fixture.project.projectId;
  const openDecisionBySubjectRef = new Map(
    fixture.openDecisions
      .filter((decision) => decision.subjectRef)
      .map((decision) => [
        decision.subjectRef as string,
        decision as OpenDecisionForDisplay
      ])
  );

  return fixture.apartmentSolutions.flatMap((solution) => {
    const edgeFeatureByRiskId = new Map<string, EdgeFeature>();

    for (const feature of solution.edgeFeatures ?? []) {
      if (!feature.needsUserDecision) {
        continue;
      }

      for (const riskId of feature.risk.riskRefs) {
        edgeFeatureByRiskId.set(riskId, feature);
      }
    }

    return solution.risks
      .filter((risk) => risk.type === "needsUserDecision" && risk.status === "open")
      .map((risk) => {
        const linkedOpenDecision = openDecisionBySubjectRef.get(risk.id);
        const linkedEdgeFeature = edgeFeatureByRiskId.get(risk.id);

        return {
          ...risk,
          apartmentTypeId: solution.apartmentTypeId,
          apartmentTypeLabel: solutionDisplayName(solution.apartmentTypeId, {
            projectId
          }),
          ...(linkedOpenDecision ? { linkedOpenDecision } : {}),
          ...(linkedEdgeFeature ? { linkedEdgeFeature } : {})
        };
      });
  });
};

const bucketsFromRisks = (records: readonly DecisionRiskRecord[]): DecisionBucket[] => {
  const buckets = new Map<string, DecisionBucket>();

  for (const record of records) {
    const key = bucketKeyFor(record);
    const theme = themeFor(record);
    const existing = buckets.get(key);

    if (existing) {
      existing.records.push(record);
      continue;
    }

    buckets.set(key, {
      key,
      theme: theme.theme,
      themeLabel: theme.label,
      records: [record]
    });
  }

  return [...buckets.values()];
};

export const projectFixtureToDecisionsHubView = (
  fixture: ProjectFixtureForDisplay
): DecisionsHubDisplayViewModel => {
  const records = detailedDecisionRisks(fixture);
  const decisions = bucketsFromRisks(records)
    .map((bucket) => decisionFromBucket(bucket, fixture.project.projectId))
    .sort((left, right) => {
      const themeDelta = left.themeLabel.localeCompare(right.themeLabel);

      if (themeDelta !== 0) {
        return themeDelta;
      }

      return left.title.localeCompare(right.title);
    });
  const groups = groupDecisions(decisions);
  const affectedTypes = new Set(
    decisions.flatMap((decision) => decision.affectedApartmentTypeIds)
  );
  const edgeNeedsUserDecisionCount = fixture.apartmentSolutions.reduce(
    (sum, solution) =>
      sum +
      (solution.edgeFeatures ?? []).filter((feature) => feature.needsUserDecision)
        .length,
    0
  );
  const resolvableDecisionCount = decisions.filter(
    (decision) => decision.resolutionStatus === "resolvable"
  ).length;
  const surfacedOnlyDecisionCount = decisions.filter(
    (decision) => decision.resolutionStatus === "surfaced_only"
  ).length;
  const core = DecisionsHubViewSchema.parse({
    projectId: fixture.project.projectId,
    status: viewStatus(fixture.project.outputBundle.status),
    decisionIds: decisions.map((decision) => decision.id),
    updatedAt: fixture.fixture.generatedAt,
    needsUserDecisionRiskCount: records.length,
    laneBOpenDecisionCount: fixture.openDecisions.filter(
      (decision) => decision.stage === "lane-b"
    ).length,
    edgeNeedsUserDecisionCount,
    groupedDecisionCount: decisions.length,
    affectedApartmentTypeCount: affectedTypes.size,
    resolvableDecisionCount,
    surfacedOnlyDecisionCount,
    decisions,
    groups,
    dataGaps: [
      "Client-ready RFI wording is not produced here; these are internal needsUserDecision rows for the specifier.",
      ...(surfacedOnlyDecisionCount > 0
        ? [
            `${surfacedOnlyDecisionCount} decision group(s) are surfaced-only because the engine has not emitted a reviewed option or structured override mapping.`
          ]
        : [])
    ],
    generatedFrom: fixture.fixture.generatedFrom
  });

  return {
    ...core,
    decisions,
    groups,
    generatedFrom: fixture.fixture.generatedFrom
  };
};

export const buildDecisionResolutionPreviewRequest = ({
  decision,
  option
}: {
  decision: DecisionsHubDecision;
  option: DecisionsHubOption;
}): {
  apartmentTypeId: string;
  actionId: string;
  label: string;
  intent: string;
  reason: string;
  structuredChange: OverrideStructuredChange;
  scopeRefId: string;
} | null => {
  if (option.resolutionStatus !== "resolvable" || !option.structuredChange) {
    return null;
  }

  const apartmentTypeId =
    option.apartmentTypeId ?? decision.affectedApartmentTypeIds[0];

  if (!apartmentTypeId) {
    return null;
  }

  return {
    apartmentTypeId,
    actionId: option.id,
    label: option.label,
    intent: `Resolve ${decision.title}: ${option.label}.`,
    reason: `Decision hub preview for ${decision.what}.`,
    structuredChange: option.structuredChange,
    scopeRefId: option.scopeRefId ?? apartmentTypeId
  };
};

export const sourceRefSummaryForDecision = (decision: DecisionsHubDecision): string =>
  sourceLabels(decision.sourceRefs).length > 0
    ? sourceLabels(decision.sourceRefs).join(", ")
    : "Source refs not produced";
